import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Opportunity } from '@app/opportunities/opportunities-api.model';
import { OpportunityFlagsService } from '@app/opportunities/services/opportunity-flags.service';
import { OpportunityModalsService } from '@app/opportunities/services/opportunity-modals.service';
import { SearchSubmitMethod } from '@app/search/services/search-tracker.service';
import { SimpleItemViewModel } from '@app/shared/models/core-view.model';
import { AuthService } from '@app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { getMatchingSkillsCount } from '@app/opportunities/utils';

export interface OpportunitiesMatchViewModel
  extends SimpleItemViewModel<Opportunity> {
  matchesCount?: number;
}

@Component({
  selector: 'dgx-search-opportunities',
  templateUrl: './search-opportunities.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchOpportunitiesComponent {
  @Input() public isLoading: boolean;
  @Input() public totalNumItems: number;
  @Input() public searchTerm: string;

  public i18n = this.translateService.instant([
    'LearningSearch_OpportunitiesTitle',
  ]);

  private _items: OpportunitiesMatchViewModel[];

  constructor(
    private authService: AuthService,
    private opportunityFlagsService: OpportunityFlagsService,
    private opportunityModalsService: OpportunityModalsService,
    private translateService: TranslateService
  ) {}

  @Input() public set items(items: OpportunitiesMatchViewModel[]) {
    this._items = items?.map((item) => {
      if (item.model) {
        const matchesCount = getMatchingSkillsCount(
          this.authService.authUser,
          item.model
        );
        const explainText = this.translateService.instant(
          'LearningSearch_OppsSidebarTooltip',
          { matches: matchesCount, totalSkills: item.model.tags.length }
        );
        return {
          ...item,
          matchesCount,
          explainText,
        };
      }
      return item;
    });
  }

  public get items(): OpportunitiesMatchViewModel[] {
    return this._items;
  }

  public get showNoResults(): boolean {
    return this.totalNumItems === 0 && !this.isLoading;
  }

  public get showError(): boolean {
    return !this.items && !this.isLoading;
  }

  public get linkData() {
    return {
      route: this.opportunityFlagsService.useOpportunityMarketplace
        ? // This `/search/` matches the specifically-created child route
          // in career.module and is redirected to `/career/opportunities/browse`
          '/career/opportunities/browse/search'
        : // This `/search` matches the wildcard route in career.module
          // and is redirected to the top-most route, which will be 'browse'
          // until the Marketplace launches.
          '/career/opportunities/search',
      searchTerm: this.searchTerm,
      collectionLink: SearchSubmitMethod.moreOpportunities,
    };
  }

  /**
   * Open a modal for the user's skills in detail.
   */
  public showSkillsDetailModal(opportunity: Opportunity) {
    this.opportunityModalsService
      .showSkillsDetailModal({
        opportunityId: opportunity.opportunityId,
        userProfileKey: this.authService.authUser.viewerProfile.userProfileKey,
        extraTrackingProperties: {
          viewMode: 'SkillsMatch',
          matchedSkillsCount: opportunity.matchedSkills?.length,
          opportunitySkillsCount: opportunity.tags?.length,
        },
      })
      .subscribe();
  }

  public trackByFn(index: number, item: OpportunitiesMatchViewModel): number {
    return item.model.opportunityId;
  }
}
